import React from 'react';
import { motion } from 'framer-motion';
import { CharacterGallery } from './CharacterGallery';
import { FamilyLineup } from './FamilyLineup';
import { NamingContrast } from './NamingContrast';
import { LogosPathos } from './LogosPathos';
import type { BriefBlock, BriefSection, BriefVisual } from '../../types/brief';
interface BriefSectionViewProps {
  section: BriefSection;
  index: number;
  /** Phone-format typography and layout. */
  compact?: boolean;
}
export function BriefSectionView({
  section,
  index,
  compact = false
}: BriefSectionViewProps) {
  const visual = section.visual ? <SectionVisual visual={section.visual} compact={compact} data-id="element-101" data-mp="27lsleiy3fvcp" /> : null;
  const lead = section.visualPlacement === 'lead';
  return <section id={section.id} className="scroll-mt-8 border-t border-[rgba(1,51,43,0.14)] pt-8 lg:pt-10" aria-labelledby={`${section.id}-title`} data-id="element-102" data-mp="2bne4wn54v7vv">
      <motion.div initial={{
      opacity: 0,
      y: 18
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      amount: 0.2
    }} transition={{
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-103" data-mp="2llae39eifsrc">
        <span className="font-body text-[10px] uppercase tracking-[0.22em] text-[rgba(1,51,43,0.4)]" data-id="element-104" data-mp="32oyr73ipu8fr">
          {String(index + 1).padStart(2, '0')}
        </span>
        <h2 id={`${section.id}-title`} className={['mt-2 max-w-[22ch] font-heading leading-[1.06] tracking-[-0.02em] text-background', compact ? 'text-[23px]' : 'text-[32px]'].join(' ')} data-id="element-105" data-mp="2tpbpegm2r0m6">
          {section.title}
        </h2>

        {lead && visual}

        <div className={compact ? 'mt-5 space-y-4' : 'mt-7 space-y-5'} data-id="element-106" data-mp="3hlz6u8nmucxv">
          {section.blocks.map((block, i) => <Block key={`${section.id}-${i}`} block={block} compact={compact} data-id="element-107" data-mp="3cneh5360bbno" data-mpi={i} />)}
        </div>
      </motion.div>

      {!lead && visual}
    </section>;
}
function SectionVisual({
  visual,
  compact
}: {
  visual: BriefVisual;
  compact: boolean;
}) {
  if (visual === 'characters') return <CharacterGallery compact={compact} data-id="element-108" data-mp="s3ziz9od2exm" />;
  if (visual === 'lineup') return <FamilyLineup compact={compact} data-id="element-109" data-mp="x2smqaduirkl" />;
  if (visual === 'pathos') return <LogosPathos compact={compact} data-id="element-110" data-mp="jb48a0g4jr0g" />;
  return <NamingContrast compact={compact} data-id="element-111" data-mp="ja30q5ybhmnh" />;
}
function Block({
  block,
  compact
}: {
  block: BriefBlock;
  compact: boolean;
}) {
  if (block.kind === 'statement') {
    return <p className={['max-w-[24ch] py-1 font-heading leading-[1.1] tracking-[-0.02em] text-background', compact ? 'text-[22px]' : 'text-[30px]'].join(' ')} data-id="element-112" data-mp="9cf2h61i5x0k">
        {block.text}
      </p>;
  }
  if (block.kind === 'aside') {
    return <p className={['max-w-[52ch] font-body leading-relaxed text-[rgba(1,51,43,0.5)]', compact ? 'text-[12px]' : 'text-[13px]'].join(' ')} data-id="element-113" data-mp="4dlnf96khxya">
        {block.text}
      </p>;
  }
  if (block.kind === 'note') {
    return <p className={['font-body font-semibold leading-snug text-background', compact ? 'text-[14px]' : 'text-[16px]'].join(' ')} data-id="element-114" data-mp="1yps9x44ww15w">
        {block.text}
      </p>;
  }
  if (block.kind === 'quote') {
    return <p className={['border-l-2 border-primary pl-4 font-body italic leading-relaxed text-[rgba(1,51,43,0.7)]', compact ? 'text-[14px]' : 'text-[17px] lg:pl-5'].join(' ')} data-id="element-115" data-mp="3t0s0ngiu6kxn">
        “{block.text}”
      </p>;
  }
  if (block.kind === 'lines') {
    return <ul className="space-y-1.5" data-id="element-116" data-mp="1snonsw6kdsi">
        {block.lines.map(line => <li key={line} className={['flex items-baseline gap-2.5 font-body leading-snug text-[rgba(1,51,43,0.85)]', compact ? 'text-[14px]' : 'text-[17px]'].join(' ')} data-id="element-117" data-mp="ftgl4j61liy9">
            <span aria-hidden="true" className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" data-id="element-118" data-mp="2hmnxpku9tmgg" />
            {line}
          </li>)}
      </ul>;
  }
  return <p className={['max-w-[64ch] font-body text-[rgba(1,51,43,0.76)]', compact ? 'text-[13.5px] leading-[1.7]' : 'text-[16px] leading-[1.75]'].join(' ')} data-id="element-119" data-mp="2f2hxno8s2g56">
      {block.text}
    </p>;
}